/**
 * 重み付き配分の編集 UI（型の配分・画像スタイルの配分で共通 — audit E3）。
 * スライダーと数値入力で各項目の % を調整し、合計 100% でないときは「均等に割り直す」を出す。
 */

import { equalWeights, mixSum, setWeight, type MixEntry } from '../../lib/weightedMix'

export function WeightedMixEditor({ title, entries, labelOf, onChange, testId, labelClassName = 'w-40' }: {
  title: string
  entries: MixEntry[]
  labelOf: (key: string) => string
  onChange: (entries: MixEntry[]) => void
  testId?: string
  labelClassName?: string
}) {
  const sum = mixSum(entries)
  return (
    <div className="mt-4 rounded-ctl border border-line p-3" data-testid={testId}>
      <p className="text-sm font-bold">{title}（合計 <span className="gm-num">100</span>% にしてください）</p>
      <div className="mt-2 space-y-3 sm:space-y-2">
        {entries.map(m => (
          // 狭い幅ではラベルを1行目、スライダーと数値を2行目に折り返す
          <div key={m.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className={`basis-full sm:basis-auto ${labelClassName}`}>{labelOf(m.key)}</span>
            <input type="range" min={0} max={100} step={5} value={m.weight}
              aria-label={`${labelOf(m.key)} の割合`}
              onChange={e => onChange(setWeight(entries, m.key, Number(e.target.value)))}
              className="min-w-0 flex-1 accent-primary" />
            <input type="number" min={0} max={100} value={m.weight}
              aria-label={`${labelOf(m.key)} の割合（%）`}
              aria-invalid={sum !== 100 ? true : undefined}
              onChange={e => onChange(setWeight(entries, m.key, Number(e.target.value)))}
              className="gm-input gm-num w-16 text-right" />
            <span className="text-ink-muted">%</span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
        {/* 合計が 100% かどうかは色だけでなく文で伝える */}
        <span className={sum === 100 ? 'text-ok' : 'text-warn'}>
          合計 <span className="gm-num">{sum}</span>%{sum === 100 ? '（OK）' : '（100% になるよう調整してください）'}
        </span>
        {sum !== 100 && (
          <button onClick={() => onChange(equalWeights(entries.map(m => m.key)))}
            className="gm-btn gm-btn-secondary gm-btn-sm">均等に割り直す</button>
        )}
      </div>
    </div>
  )
}
